import { Chain } from '@1delta/chain-registry'
import { multicallRetryUniversal } from '@1delta/providers'
import type { Address } from 'viem'
import { YieldFetcher } from '../../types'
import { fetchDefiLlamaApy } from './defillama'

/**
 * Saturn `sUSDat` — the INCOME leg, on-chain, and a row that says so.
 *
 * sUSDat stakes USDat (an M^0 extension token over tokenized Treasuries) into
 * **Strategy's STRC**, the variable-rate perpetual preferred stock, and passes
 * the dividend through. The vault has TWO legs that both land in
 * `totalAssets()`:
 *
 *     totalAssets = usdatBalance + (strcBalance − unvested) × STRC_ORACLE.price
 *
 *  1. **Income.** A `PROCESSOR_ROLE` calls `transferInRewards(strc)`, vesting
 *     linearly over `vestingPeriod()`. Clockwork: **50 distributions in 152.3
 *     days at a 3.11-day cadence, 39,665.92 STRC ($3.88 M) on $76.4 M average
 *     TVL = 12.19 % APR** (measured 2026-09-10, whole life).
 *  2. **The mark.** 96.5 % of NAV is STRC held outright and marked to an
 *     oracle — 723,435.68 STRC against $2.55 M of actual USDat. Preferred stock
 *     moves, and this one did: the share price ran 1.000000 → 0.779150 →
 *     1.017628 over the same period, i.e. **+3.73 % realized with a −22.82 %
 *     max drawdown**.
 *
 * **This fetcher measures leg 1.** The mark is carried by the row instead —
 * `yieldProfile: 'volatile'` (`VOLATILE_VAULT_OVERRIDES`) puts `nav-drawdown`
 * in `termSheet.supply.principal.risks`, and the registry `description` states
 * the drawdown in words. Per AGENTS.md: *where `totalAssets` carries a mark,
 * the APR field is an income statement and `convertToAssets` is the balance
 * sheet* — so the field says what the position EARNED, and the row says the
 * principal can move. Neither alone is the return.
 *
 * ## Why not a trailing REALIZED rate
 *
 * That was built first and measured, which is the only reason this docstring
 * can be definite: over a 90-day window on 2026-09-10 it read **+51.75 %**, and
 * a 30-day window read the same order. Both are the recovery out of the June
 * trough annualised — an amplification of a mark, not a measurement of income,
 * and the mirror image of the −80 % it would print on the way down. That is the
 * Strata-junior pathology ("absurdly positive while a marked-down collateral
 * recovers"), and unlike Yield Basis — whose realized 30-day figures sit at
 * −0.5 % to +1.6 %, i.e. noise scale — this vault's mark swings ±20 %, so the
 * trailing figure is noise-DOMINATED rather than noise-adjacent. It also needed
 * archival RPC on a path `fetchSavingsVaults` awaits, which is the wren latency
 * trap for a number that was worse anyway.
 *
 * ## Units and the small upward bias
 *
 * `vestingAmount` / `getUnvestedAmount` are in **STRC units at 6 decimals**
 * (they are subtracted from `strcBalance`, not from a USDat balance) — reading
 * them as USDat understates the leg ~100×. The oracle answers
 * `getPrice() → (price, decimals)`, its own shape, NOT Chainlink's
 * (`latestRoundData`/`decimals`/`latestAnswer` all revert on it); the address
 * is read from `getStrcOracle()` rather than pinned.
 *
 * Annualising by `vestingPeriod` assumes a new distribution lands the moment
 * the last one finishes vesting. `transferInRewards` reverts `StillVesting()`
 * before then, so the real cadence can only be LONGER — measured at 3.11 days
 * against a 3.00-day period, which makes this **~3.7 % relative high** (13.51 %
 * against 13.03 % at the observed cadence). The BitFi epoch error in miniature.
 * It is not corrected here because the contract keeps no distribution history
 * to measure the cadence from, and a pinned 3.11 would go stale; treat the
 * figure as a small ceiling on the income leg.
 *
 * ## Fallback
 *
 * DefiLlama's `saturn` / `SUSDAT` pool measures the SAME leg (15.58 % against
 * this formula's 13.51 % on 2026-09-10 — the gap is their compounding and
 * window, not a different quantity), so it is a legitimate second source in a
 * way it would NOT have been for a realized rate. That distinction is the
 * Vesper lesson: a fallback may degrade precision, never swap the measurement.
 */

const SUSDAT: Address = '0xd166337499e176bbc38a1fbd113ab144e5bd2df7'
/** `saturn` / `SUSDAT` on Ethereum. */
const DEFILLAMA_POOL = '47e72726-3b2b-4fe6-b4a9-a26e1fcd9a50'

const YEAR_SECONDS = 31_536_000
/** STRC amounts on this vault are 6-decimal, like USDat. */
const STRC_UNIT = 1e6

/**
 * The token-list `assetGroup` on Ethereum, where the only real vault is.
 *
 * BNB (56) and Monad (143) carry same-address LayerZero OFT mirrors under a
 * DIFFERENT group spelling whose entire 4626 surface reverts. Both keys are
 * emitted so the intrinsic-yield join reaches the bridged supply too (Monad
 * alone holds ~6.08 M sUSDat) — the Cap `stcUSD` lesson: one vault, several
 * groups, and a case-sensitive index between them.
 */
export const SUSDAT_KEY = 'Saturn sUSDat::SUSDAT'
/** The spelling chains 56 and 143 use for the same product. */
export const SUSDAT_BRIDGED_KEY = 'Staked Saturn USD::sUSDat'

const VAULT_ABI = [
  {
    name: 'vestingAmount',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    name: 'vestingPeriod',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    name: 'totalAssets',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    name: 'getStrcOracle',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'address' }],
  },
] as const

/** Saturn's own shape — deliberately not Chainlink's. */
const STRC_ORACLE_ABI = [
  {
    name: 'getPrice',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [
      { name: 'price', type: 'uint256' },
      { name: 'decimals', type: 'uint8' },
    ],
  },
] as const

/** Accept both decoder shapes for `getPrice() → (uint256, uint8)`. */
const decodeStrcPrice = (
  cell: unknown,
): { price: bigint; priceDecimals: number } => {
  if (Array.isArray(cell) && cell.length >= 2) {
    return { price: BigInt(cell[0] as never), priceDecimals: Number(cell[1]) }
  }
  if (cell && typeof cell === 'object') {
    const o = cell as { price?: unknown; decimals?: unknown }
    if (o.price !== undefined && o.decimals !== undefined) {
      return {
        price: BigInt(o.price as never),
        priceDecimals: Number(o.decimals),
      }
    }
  }
  throw new Error('sUSDat: unrecognised STRC oracle response shape')
}

/** Nominal APR in percent from the vesting state. Throws on a bad read so the
 *  caller can fall back rather than publish a zero that looks like a rate. */
const computeIncomeApr = async (): Promise<number> => {
  const [vestingAmount, vestingPeriod, totalAssets, oracle] =
    (await multicallRetryUniversal({
      chain: Chain.ETHEREUM_MAINNET,
      abi: VAULT_ABI,
      calls: [
        { address: SUSDAT, name: 'vestingAmount', params: [] },
        { address: SUSDAT, name: 'vestingPeriod', params: [] },
        { address: SUSDAT, name: 'totalAssets', params: [] },
        { address: SUSDAT, name: 'getStrcOracle', params: [] },
      ],
      allowFailure: false,
    })) as [bigint, bigint, bigint, Address]

  if (vestingPeriod <= 0n || totalAssets <= 0n) {
    throw new Error('sUSDat: empty vault or zero vesting period')
  }

  // ONE call with TWO outputs, so the response is `[[price, decimals]]` — the
  // multicall returns one cell per CALL, not per output. A decoder that keeps
  // the named-output shape hands back an object instead, so accept both rather
  // than lose the leg to a decoder detail (the `value_oracle` precedent in
  // `readers/yieldBasisLt.ts`). Destructuring the outer array directly is the
  // bug this comment exists to prevent: it silently binds `decimals` to
  // `undefined`, `10 ** NaN` is `NaN`, and the whole fetcher falls through to
  // the fallback with no error anyone sees.
  const [oracleCell] = (await multicallRetryUniversal({
    chain: Chain.ETHEREUM_MAINNET,
    abi: STRC_ORACLE_ABI,
    calls: [{ address: oracle, name: 'getPrice', params: [] }],
    allowFailure: false,
  })) as unknown[]

  const { price, priceDecimals } = decodeStrcPrice(oracleCell)
  if (price <= 0n) throw new Error('sUSDat: STRC oracle returned no price')

  // Both legs are small enough to leave integer space safely: the reward is
  // ~1e9 raw and the price ~1e10 raw.
  const strcPriceUsd = Number(price) / 10 ** Number(priceDecimals)
  const rewardUsd = (Number(vestingAmount) / STRC_UNIT) * strcPriceUsd
  const cyclesPerYear = YEAR_SECONDS / Number(vestingPeriod)
  // `totalAssets` is 6-decimal USDat, i.e. dollars.
  const tvlUsd = Number(totalAssets) / 1e6

  const apr = ((rewardUsd * cyclesPerYear) / tvlUsd) * 100
  if (!Number.isFinite(apr) || apr < 0) {
    throw new Error('sUSDat: implausible income APR')
  }
  return apr
}

export const saturnFetcher: YieldFetcher = {
  label: 'SATURN_SUSDAT',
  fetch: async () => {
    const apr = await computeIncomeApr()
      .catch(() => fetchDefiLlamaApy(DEFILLAMA_POOL))
      .catch(() => 0)
    return { [SUSDAT_KEY]: apr, [SUSDAT_BRIDGED_KEY]: apr }
  },
}
